import { Button, Typography, IconButton, Grid, Avatar } from "@material-ui/core";
import React from "react";
import "./ComprehensionQuestionView.css";
import ComprehensionQuestion from "../../../interfaces/ComprehensionQuestion";
import FirebaseService from "../../../services/firebase/FirebaseService";
import RecorderService from "../../../services/recorder/RecorderService";
import UploadService from "../../../services/upload/UploadService";
import LogService from "../../../services/LogService";
import uuid from "react-uuid";
import { startRecording, stopRecording } from "./state/BookScreenState";
import ReactPlayer from "react-player";
import handleDuration from "../../../services/recorder/HandleDuration";
import { Pause, PlayArrow, Delete, Mic } from "@material-ui/icons";
import Microphone from "../../../assets/icons/Microphone.png";
import AudioWave from "../../../assets/icons/audioWave.png";
import CheckMark from "../../../assets/icons/checkMark.png";
import BookScreenButton from "./BookScreenButton";
import Loading from "../../Loading/Loading";
import ComprehensionMessage from "../../../interfaces/ComprehensionMessage";

interface ComprehensionQuestionViewProps {
  question: ComprehensionQuestion;
  classId: string;
  assignmentId: string;
  currentUserId: string;
  audioDevice?: string;
  replyType: string | undefined;
}

interface ComprehensionQuestionViewState {
  response: string;
  comments?: ComprehensionMessage | null;
  data: Blob;
  duration: number;
  isRecording: boolean;
  questionId: string;
  users: Map<string, string>;
  replyType: string;
  isLoadingQuestion: boolean;
}

interface AudioCardProps {
  audioPath: string;
  label: string;
}

interface AudioCardState {
  audioUrl: string;
  isPlaying: boolean;
  duration: number;
}

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MAX_RECORDING_MINUTES = 30;
const RECORDER_SERVICE_DURATION = MAX_RECORDING_MINUTES * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;

class ComprehensionQuestionView extends React.Component<
  ComprehensionQuestionViewProps,
  ComprehensionQuestionViewState
> {
  recorderService: RecorderService;
  commentWatcher?: () => void = undefined;

  defaultState: ComprehensionQuestionViewState = {
    response: "",
    comments: null,
    data: new Blob([]),
    duration: 0,
    isRecording: false,
    questionId: "",
    users: new Map(),
    replyType: "",
    isLoadingQuestion: false,
  };
  constructor(props: ComprehensionQuestionViewProps) {
    super(props);
    this.state = { ...this.defaultState };
    this.recorderService = new RecorderService();
  }

  override componentDidMount() {
    this.loadQuestion();

    this.recorderService.initialize(
      RECORDER_SERVICE_DURATION,
      this.props.audioDevice,
      async (data, duration) => {
        try {
          this.setState(state => {
            return {
              ...state,
              data: data,
              isRecording: true,
              duration: duration,
            };
          });
        } catch (e) {
          LogService.exception(e, `ComprehensionQuestionView.startRecording`, "Failed to initialize recording page");
        }
      },
      stopRecording,
    );
  }

  loadQuestion() {
    this.setState({
      ...this.defaultState,
      replyType: this.props.question.replyType ?? this.props.replyType ?? "text",
      questionId: this.props.question.id,
      isLoadingQuestion: true,
    });

    if (this.commentWatcher) {
      this.commentWatcher();
      this.commentWatcher = undefined;
    }

    this.storeUsers().then(() => {
      this.setState({ isLoadingQuestion: false });
      this.commentWatcher = FirebaseService.watchUserComment(this.props.currentUserId, this.props.question.id, v => {
        this.setState({ isLoadingQuestion: false });
        this.setState({
          comments: v,
          data: new Blob([]),
        });
        this.storeUsers();
      });
    });
  }

  startRecording = async () => {
    this.setState(state => {
      return { ...state, hasRecordedThisPage: true };
    });
    startRecording();
    this.recorderService.startRecording();
  };

  stopRecording = async () => {
    stopRecording();
    await this.recorderService.stopRecording();
  };

  deleteRecording = async () => {
    if (this.state.isRecording) {
      stopRecording();
      await this.recorderService.stopRecording();
    }
    this.setState(state => {
      return {
        ...state,
        data: new Blob(),
        isRecording: false,
      };
    });
  };

  uploadRecording = (questionId: string) => {
    // return immediately if no recording exists
    if (this.state.data.size === 0) return;

    const uploadPath = this.props.assignmentId + "/assignment_comments/" + uuid();

    UploadService.uploadCommentAudioData(this.state.data, uploadPath, Math.floor(this.state.duration / 100) / 10).then(
      () =>
        FirebaseService.addComprehensionResponse(
          this.props.classId,
          this.props.currentUserId,
          questionId,
          this.props.assignmentId,
          {
            commentType: "oral",
            audioPath: uploadPath,
          },
        ).then(() => this.deleteRecording()),
    );
  };

  async storeUsers() {
    this.state.comments?.responses?.forEach(async e => {
      if (!this.state.users.has(e.userId)) {
        const temp = this.state.users;
        temp.set(e.userId, (await FirebaseService.getUser(e.userId as string)).displayName);
        this.setState({
          users: temp,
        });
      }
    });
  }

  oldComments = () =>
    this.state.comments?.responses.map(value => (
      <div key={value.timeStamp}>
        {value.comment.commentType === "text" ? (
          <div className="textCard">{value.comment.textComment}</div>
        ) : (
          <ComprehensionAudioCard label="Response" audioPath={value.comment.audioPath as string} />
        )}
        <div style={{ height: "5px" }} />
        <div
          style={{
            textAlign: "right",
            fontSize: "12px",
          }}
        >
          {this.state.users.get(value.userId) ?? "Loading..."}
        </div>
        <div style={{ height: "20px" }} />
      </div>
    ));

  override componentWillUnmount() {
    if (this.commentWatcher) {
      this.commentWatcher();
    }

    this.stopRecording();
    this.recorderService.cleanupAudio();
  }

  override componentDidUpdate(prevProps: ComprehensionQuestionViewProps) {
    const question = prevProps as { question: { id: string } };
    if (question.question.id !== this.props.question.id) {
      this.loadQuestion();
    }
  }

  textResponseHandler = (response: string) => this.setState({ response });

  override render() {
    return this.state.isLoadingQuestion ? (
      <Loading />
    ) : (
      <div className="comprehensionQuestionView">
        <Typography component="div" className="questionTitle">
          {this.props.question?.commentType === "text" ? (
            this.props.question?.textComment
          ) : (
            <ComprehensionAudioCard
              label="Listen to the Question"
              audioPath={this.props.question?.audioPath as string}
            />
          )}
        </Typography>
        <div>
          {this.oldComments()}

          {this.state.replyType === "oral" && (
            <div>
              <div style={{ paddingBottom: "8px" }}>
                <ComprehensionAudioRecorder
                  getBlob={() => this.state.data}
                  isSubmitted={false}
                  pageTitle="hi"
                  pageIndex={1}
                  onDeleteRecording={this.deleteRecording}
                  onStartRecording={this.startRecording}
                  onStopRecording={this.stopRecording}
                />
              </div>
              <div style={{ float: "right" }}>
                <BookScreenButton
                  tooltip="Submit your recording"
                  text="Submit"
                  onPressed={() => this.uploadRecording(this.props.question?.id)}
                />
              </div>
            </div>
          )}
          {this.state.replyType === "text" && (
            <div>
              <textarea
                value={this.state.response}
                style={{
                  width: "100%",
                  paddingBottom: "50px",
                  fontFamily: "Futura",
                  outlineColor: "white",
                  borderRadius: "4px",
                  minWidth: "100%",
                  minHeight: "200px",
                }}
                placeholder="Enter your answer here..."
                onChange={v => this.textResponseHandler(v.target.value)}
              />
              <div style={{ float: "right" }}>
                <BookScreenButton
                  tooltip="Submit your response"
                  text="Submit"
                  onPressed={() => {
                    if (this.state.response)
                      FirebaseService.addComprehensionResponse(
                        this.props.classId,
                        this.props.currentUserId,
                        this.props.question?.id,
                        this.props.assignmentId,
                        {
                          commentType: "text",
                          textComment: this.state.response,
                        },
                      ).then(() => this.setState({ response: "" }));
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

class ComprehensionAudioCard extends React.Component<AudioCardProps, AudioCardState> {
  _isMounted = true;

  constructor(props: AudioCardProps) {
    super(props);

    this.state = {
      audioUrl: "",
      isPlaying: false,
      duration: 0,
    };
  }

  override componentDidMount() {
    UploadService.getCommentAudio(this.props.audioPath).then(async audioUrl => {
      if (this._isMounted) {
        this.setState({ audioUrl });
      }
    });
  }

  override componentWillUnmount() {
    this._isMounted = false;
  }

  handleAudio = () => this.setState(state => ({ isPlaying: !state.isPlaying }));

  loadDuration = (duration: number) => this.setState({ duration });

  override render() {
    return (
      <div>
        <ReactPlayer
          width="0px"
          height="0px"
          url={this.state.audioUrl}
          playing={this.state.isPlaying}
          onDuration={this.loadDuration}
          onEnded={this.handleAudio}
        />
        <Grid container direction="row" justify="space-between" alignItems="center">
          <div className="audioCard">
            <div className="leftText" style={{ float: "left", marginTop: "12px" }}>
              <Typography className="teachersInstructions">{this.props.label}</Typography>
              <Typography className="audioTime">{`(${handleDuration(this.state.duration)})`}</Typography>
            </div>
            <IconButton style={{ float: "right" }} className="playPauseButton" onClick={this.handleAudio}>
              {" "}
              {this.state.isPlaying ? <Pause /> : <PlayArrow />}{" "}
            </IconButton>
          </div>
        </Grid>
      </div>
    );
  }
}

interface QueueCardProps {
  pageTitle: string;
  pageIndex: number;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onDeleteRecording: () => void;
  getBlob: () => Blob | undefined;
  isSubmitted: boolean;
}

interface QueueCardState {
  isGettingReadyToRecording: boolean;
  isRecording: boolean;
  isPlaying: boolean;
  buttonTimer: number;
}

class ComprehensionAudioRecorder extends React.Component<QueueCardProps, QueueCardState> {
  constructor(props: QueueCardProps) {
    super(props);
    this.state = {
      isGettingReadyToRecording: false,
      isRecording: false,
      isPlaying: false,
      buttonTimer: 3,
    };
  }

  startTimer = () => {
    this.setState({
      isGettingReadyToRecording: true,
    });

    const runCountDown = async () => {
      if (this.state.buttonTimer === 0) {
        this.props.onStartRecording();
        this.setState({
          isRecording: true,
          isGettingReadyToRecording: false,
          buttonTimer: 3,
        });
      } else {
        this.setState({
          buttonTimer: this.state.buttonTimer - 1,
        });
        setTimeout(runCountDown, 1000);
      }
    };

    setTimeout(() => {
      runCountDown();
    }, 1000);
  };

  override componentWillUnmount() {
    this.props.onStopRecording();
  }

  override render() {
    return (
      <div>
        {!this.state.isRecording && !this.state.isGettingReadyToRecording ? (
          //check to see if submitted
          !(this.props.getBlob() === undefined || this.props.getBlob()?.size === 0) ? (
            <div className="submittedCard active">
              <Avatar className="circleCheckMark">
                <img src={CheckMark} alt="Check Mark" className="circleImage" />
              </Avatar>
              <div className="cardText">
                <Typography className="pageTitle">Record</Typography>
              </div>
              <div className="rightButtonLayout">
                <Button
                  className="rightActionButton"
                  onClick={() => {
                    this.props.onDeleteRecording();
                  }}
                >
                  <Delete className="rightActionIcon" />
                </Button>
                <Button className="rightActionButton" onClick={this.startTimer}>
                  <Mic className="rightActionIcon" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="queueCard active">
              <Avatar className="circleMicIcon">
                <img src={Microphone} alt="Microphone" className="circleImage" />
              </Avatar>
              <div className="cardText">
                <Typography className="pageTitle">Record</Typography>
              </div>
              <Button className="button" onClick={this.startTimer}>
                <Typography className="buttonText">Start!!</Typography>
              </Button>
            </div>
          )
        ) : this.state.isGettingReadyToRecording ? (
          <div className="active queueCard">
            <Avatar className="activeAvatar">
              <div className="buttonText">{this.state.buttonTimer}</div>
            </Avatar>
            <Typography className="pageTitle">Start Speaking in...</Typography>
          </div>
        ) : (
          <div className="active queueCard">
            <Button
              onClick={() => {
                this.props.onStopRecording();

                this.setState({
                  isRecording: false,
                });
              }}
            >
              <Avatar className="activeAvatar">
                <Pause />
              </Avatar>
            </Button>
            <div className="audioWaveContainer">
              <img src={AudioWave} alt="Audio Progress" className="audioWaveImage" />
              <img src={AudioWave} alt="Audio Progress" className="audioWaveImage" />
              <img src={AudioWave} alt="Audio Progress" className="audioWaveImage" />
            </div>

            <Typography className="rightAudioDuration">recording...</Typography>
          </div>
        )}
      </div>
    );
  }
}

export default ComprehensionQuestionView;
